import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const BulkUpload = ({ categories, onDone }) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState(categories[0]?.slug || "");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/"));
    if (imgs.length !== list.length) toast.error("Yalnızca görsel dosyaları kabul edilir");
    setFiles((prev) => [...prev, ...imgs]);
  };

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("category", category);
      fd.append("create_products", "true");
      const { data } = await api.post("/upload", fd);
      toast.success(`${data.length} ürün yüklendi`);
      setFiles([]);
      onDone();
    } catch {
      toast.error("Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 p-8" data-testid="bulk-upload-tab">
      <div className="max-w-md mb-6">
        <Label className="mb-2 block">Kategori</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="bulk-category-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-14 text-center cursor-pointer transition-colors duration-300 ${
          dragging ? "border-wood-light bg-beige-soft" : "border-stone-300 hover:border-stone-500"
        }`}
        data-testid="bulk-dropzone"
      >
        <UploadCloud size={36} className="mx-auto text-stone-400 mb-4" />
        <p className="text-stone-700 font-medium">Görselleri buraya sürükleyin</p>
        <p className="text-stone-400 text-sm font-light mt-1">veya tıklayarak seçin — çoklu seçim desteklenir</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="bulk-file-input"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-6">
          <p className="text-sm text-stone-500 mb-3">{files.length} dosya hazır</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {files.map((f, i) => (
              <div key={i} className="relative group">
                <img src={URL.createObjectURL(f)} alt={f.name} className="h-20 w-full object-cover bg-beige-soft" />
                <button
                  onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 bg-stone-900 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`bulk-remove-${i}`}
                  aria-label="Kaldır"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <Button
            onClick={upload}
            disabled={uploading || !category}
            className="mt-6 bg-stone-900 hover:bg-wood text-stone-50 rounded-none"
            data-testid="bulk-upload-button"
          >
            {uploading ? "Yükleniyor..." : `${files.length} Görseli Yükle`}
          </Button>
        </div>
      )}
    </div>
  );
};
